import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  usePurchaseOrders, usePurchaseReceipts, usePurchaseReturns,
  useConfirmPO, usePostReceipt, usePostReturn,
  useCreatePO, useCreateReceipt, useCreateReturn, useSuppliers, useBillPO,
} from '../hooks/useProcurementApi';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ShoppingCart, Package, Plus, RotateCcw, Truck, ClipboardCheck, AlertTriangle, ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import toast from 'react-hot-toast';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { LineItemsEditor, type DocumentLine } from '@/shared/components/commerce/LineItemsEditor';
import { formatINR } from '@/shared/utils/currency';

interface StatChipProps {
  label: string;
  value: string | number;
  sublabel?: string;
  icon: React.ReactNode;
  color: string;
  index: number;
}

function StatChip({ label, value, sublabel, icon, color, index }: StatChipProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: index * 0.04 }}
      whileHover={{ y: -2 }}
      className="group"
    >
      <Card className="p-4 transition-shadow duration-200 ease-standard hover:shadow-surface-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground truncate">{label}</p>
            <p className="text-2xl font-bold text-foreground mt-1 tabular-nums">{value}</p>
            {sublabel && <p className="text-[11px] text-muted-foreground mt-0.5">{sublabel}</p>}
          </div>
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 ease-standard group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${color}29, ${color}0D)`,
              boxShadow: `0 0 0 1px ${color}26`,
              color,
            }}
          >
            {icon}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

const textareaClass =
  'w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40';

const TABS = [{ id: 'orders', label: 'Purchase Orders', icon: ShoppingCart }, { id: 'receipts', label: 'Goods Received', icon: Package }, { id: 'returns', label: 'Purchase Returns', icon: RotateCcw }];

/**
 * Deprecation notice for the V1 Procurement screen.
 *
 * The client audit found two independent, live purchase-order systems; the
 * decision was to consolidate on Procure-to-Pay, which adds the requisition and
 * approval steps, three-way match, RFQ and supplier scorecards this screen has
 * never had. Nothing here is removed — the screen and its API are untouched and
 * keep working — so this banner is how a user finds out where the work moved.
 *
 * Dismissal is remembered in localStorage rather than component state, so it
 * stays dismissed across navigation instead of reappearing on every visit. A
 * localStorage failure (private mode, quota) degrades to "always show" rather
 * than breaking the page.
 */
const P2P_BANNER_DISMISS_KEY = 'zynx.procurement.v1DeprecationDismissed';

function ProcurementDeprecationBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(P2P_BANNER_DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(P2P_BANNER_DISMISS_KEY, '1'); } catch { /* non-fatal */ }
  };

  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800/60 dark:bg-amber-950/30">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
      <div className="min-w-0 flex-1 text-sm text-amber-900 dark:text-amber-200">
        <p className="font-semibold">This screen has been superseded by Procure-to-Pay.</p>
        <p className="mt-1 text-amber-800 dark:text-amber-300">
          Purchasing is consolidating onto Procure-to-Pay, which adds purchase requisitions and
          approvals, three-way matching against goods receipts and supplier bills, multi-supplier
          RFQ comparison and supplier scorecards. Please raise new purchases there. Everything on
          this screen still works, and existing records are unaffected.
        </p>
        <Link
          to={ROUTES.accountingV2P2p}
          className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950 dark:text-amber-100"
        >
          Go to Procure-to-Pay <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss deprecation notice"
        className="shrink-0 rounded-md p-1 text-amber-700 transition hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export function ProcurementPage() {
  const [tab, setTab] = useState('orders');

  // Reuses the same react-query hooks the tabs below call — cached, so this
  // does not add any extra network requests.
  const { data: ordersData } = usePurchaseOrders();
  const { data: receiptsData } = usePurchaseReceipts();
  const { data: returnsData } = usePurchaseReturns();

  const orders = ordersData?.data || [];
  const receipts = receiptsData?.data || [];
  const returns = returnsData?.data || [];

  const awaitingReceipt = orders.filter(o => ['ordered', 'partially_received'].includes(o.status)).length;
  const receiptsPosted = receipts.filter(r => r.status === 'posted').length;
  const returnsPosted = returns.filter(r => r.status === 'posted').length;

  const hasData = orders.length > 0 || receipts.length > 0 || returns.length > 0;

  return (
    <div className="p-6 space-y-4">
      <div><h1 className="text-2xl font-semibold tracking-tight">Procurement</h1><p className="text-sm text-muted-foreground mt-1">Purchase orders, goods receipts and supplier returns</p></div>

      <ProcurementDeprecationBanner />

      {hasData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatChip index={0} label="Purchase Orders" value={orders.length} sublabel="Total recorded" icon={<ShoppingCart className="h-5 w-5" />} color="#6E62FF" />
          <StatChip index={1} label="Awaiting Receipt" value={awaitingReceipt} sublabel="Ordered, not yet received" icon={<Truck className="h-5 w-5" />} color="#f59e0b" />
          <StatChip index={2} label="Receipts Posted" value={receiptsPosted} sublabel={`of ${receipts.length} GRNs`} icon={<Package className="h-5 w-5" />} color="#10b981" />
          <StatChip index={3} label="Returns Posted" value={returnsPosted} sublabel={`of ${returns.length} returns`} icon={<ClipboardCheck className="h-5 w-5" />} color="#06b6d4" />
        </div>
      )}

      <ModuleGuide
        summary="The full flow from ordering goods to paying the supplier. An order is only a commitment — stock increases when the Goods Received note is posted."
        steps={['Purchase Order: record the supplier, quantities and agreed rates.', 'Goods Received (GRN): capture the actual received quantity; posting it updates Inventory.', 'Purchase Bill: creates the accounts payable; vendor payments reduce the due.']}
        formulas={[
          { label: 'Order total', formula: 'Σ (Quantity × Rate − Discount) + Tax' },
          { label: 'Pending receipt', formula: 'Ordered quantity − Received quantity' },
        ]}
      />
      <div className="flex gap-2 border-b overflow-x-auto">
        {TABS.map(t => (<button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}><t.icon className="h-4 w-4" />{t.label}</button>))}
      </div>
      {tab === 'orders' && <OrdersTab />}
      {tab === 'receipts' && <ReceiptsTab />}
      {tab === 'returns' && <ReturnsTab />}
    </div>
  );
}

/* ── New Purchase Order ──────────────────────────────────────────────────── */
function NewPurchaseOrderModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: suppliers = [] } = useSuppliers(open);
  const createPO = useCreatePO();
  const [supplierId, setSupplierId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DocumentLine[]>([]);

  const submit = () => {
    if (!supplierId) { toast.error('Select a supplier.'); return; }
    if (lines.length === 0) { toast.error('Add at least one product line.'); return; }
    createPO.mutate(
      { supplierId, expectedDate: expectedDate || undefined, paymentTerms, notes, lines },
      {
        onSuccess: () => {
          toast.success('Purchase order saved as draft.');
          setSupplierId(''); setExpectedDate(''); setNotes(''); setLines([]);
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the purchase order.')),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Purchase Order"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={createPO.isPending}>
            {createPO.isPending ? 'Saving…' : 'Save Draft'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Select
            label="Supplier"
            name="po-supplier"
            options={suppliers.map(s => ({ label: s.name, value: s.id }))}
            placeholder={suppliers.length ? 'Select a supplier' : 'No suppliers found'}
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          />
          <Input label="Expected date" name="po-expected" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
          <Input label="Payment terms" name="po-terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
        </div>
        {suppliers.length === 0 && (
          <p className="text-body-sm text-on-surface-variant">
            No suppliers exist yet. Add vendors under Accounts → Parties first.
          </p>
        )}
        <LineItemsEditor lines={lines} onChange={setLines} priceField="purchasePrice" priceLabel="Cost" />
        <div>
          <label htmlFor="po-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">Notes</label>
          <textarea id="po-notes" rows={2} className={textareaClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <p className="text-body-sm text-on-surface-variant">
          Saved as a draft. Confirming the order commits it to the supplier; stock only changes when a
          Goods Received note is posted.
        </p>
      </div>
    </Modal>
  );
}

/* ── New Goods Received Note ─────────────────────────────────────────────── */
function NewReceiptModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data } = usePurchaseOrders();
  const createReceipt = useCreateReceipt();
  const [orderId, setOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const receivable = (data?.data || []).filter(o => ['ordered', 'partially_received'].includes(o.status));
  const selected = receivable.find(o => o._id === orderId);

  const submit = () => {
    if (!selected) { toast.error('Select a confirmed purchase order.'); return; }
    const lines = (selected.lines || []).map((l: { productId?: { _id?: string } | string; quantity?: number }) => {
      const pid = typeof l.productId === 'string' ? l.productId : l.productId?._id ?? '';
      return { productId: pid, receivedQuantity: Number(quantities[pid] ?? l.quantity ?? 0) };
    }).filter((l: { receivedQuantity: number }) => l.receivedQuantity > 0);
    if (lines.length === 0) { toast.error('Enter at least one received quantity.'); return; }
    createReceipt.mutate(
      { purchaseOrderId: orderId, lines, notes },
      {
        onSuccess: () => {
          toast.success('Goods received note saved as draft.');
          setOrderId(''); setQuantities({}); setNotes('');
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the receipt.')),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Goods Received Note"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={createReceipt.isPending}>
            {createReceipt.isPending ? 'Saving…' : 'Save Draft'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Select
          label="Purchase order"
          name="grn-po"
          options={receivable.map(o => ({ label: `${o.orderNumber || 'Draft'} — ${o.supplierId?.name ?? 'Supplier'}`, value: o._id }))}
          placeholder={receivable.length ? 'Select a confirmed order' : 'No confirmed orders awaiting receipt'}
          value={orderId}
          onChange={(e) => { setOrderId(e.target.value); setQuantities({}); }}
        />
        {selected && (
          <div className="overflow-x-auto rounded-lg border border-outline">
            <table className="w-full text-left text-body-sm">
              <caption className="sr-only">Received quantities</caption>
              <thead className="bg-surface-variant text-on-surface-variant">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">Product</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Ordered</th>
                  <th scope="col" className="w-32 px-3 py-2 text-right font-medium">Received</th>
                </tr>
              </thead>
              <tbody>
                {(selected.lines || []).map((l: { productId?: { _id?: string; name?: string } | string; quantity?: number }, i: number) => {
                  const pid = typeof l.productId === 'string' ? l.productId : l.productId?._id ?? '';
                  const pname = typeof l.productId === 'string' ? pid : l.productId?.name ?? 'Product';
                  return (
                    <tr key={`${pid}-${i}`} className="border-t border-outline">
                      <td className="px-3 py-2 text-on-surface">{pname}</td>
                      <td className="px-3 py-2 text-right text-on-surface-variant">{l.quantity ?? 0}</td>
                      <td className="px-3 py-2">
                        <Input
                          aria-label={`Received quantity for ${pname}`}
                          type="number"
                          min="0"
                          className="text-right"
                          value={String(quantities[pid] ?? l.quantity ?? 0)}
                          onChange={(e) => setQuantities(q => ({ ...q, [pid]: Math.max(0, Number(e.target.value) || 0) }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <label htmlFor="grn-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">Notes</label>
          <textarea id="grn-notes" rows={2} className={textareaClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <p className="text-body-sm text-on-surface-variant">
          Posting the note is what increases inventory — a draft does not affect stock.
        </p>
      </div>
    </Modal>
  );
}

/* ── New Purchase Return ─────────────────────────────────────────────────── */
function NewReturnModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data } = usePurchaseOrders();
  const createReturn = useCreateReturn();
  const [orderId, setOrderId] = useState('');
  const [reason, setReason] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const returnable = (data?.data || []).filter(o => ['partially_received', 'received', 'billed', 'paid'].includes(o.status));
  const selected = returnable.find(o => o._id === orderId);

  const submit = () => {
    if (!selected) { toast.error('Select a received purchase order.'); return; }
    const lines = (selected.lines || []).map((l: { productId?: { _id?: string } | string }) => {
      const pid = typeof l.productId === 'string' ? l.productId : l.productId?._id ?? '';
      return { productId: pid, returnedQuantity: Number(quantities[pid] ?? 0) };
    }).filter((l: { returnedQuantity: number }) => l.returnedQuantity > 0);
    if (lines.length === 0) { toast.error('Enter at least one return quantity.'); return; }
    if (!reason.trim()) { toast.error('A return reason is required.'); return; }
    createReturn.mutate(
      { purchaseOrderId: orderId, lines, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('Purchase return saved as draft.');
          setOrderId(''); setQuantities({}); setReason('');
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the return.')),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Purchase Return"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={createReturn.isPending}>
            {createReturn.isPending ? 'Saving…' : 'Save Draft'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Select
          label="Purchase order"
          name="ret-po"
          options={returnable.map(o => ({ label: `${o.orderNumber || 'Draft'} — ${o.supplierId?.name ?? 'Supplier'}`, value: o._id }))}
          placeholder={returnable.length ? 'Select a received order' : 'No received orders to return against'}
          value={orderId}
          onChange={(e) => { setOrderId(e.target.value); setQuantities({}); }}
        />
        {selected && (
          <div className="overflow-x-auto rounded-lg border border-outline">
            <table className="w-full text-left text-body-sm">
              <caption className="sr-only">Return quantities</caption>
              <thead className="bg-surface-variant text-on-surface-variant">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">Product</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Ordered</th>
                  <th scope="col" className="w-32 px-3 py-2 text-right font-medium">Return qty</th>
                </tr>
              </thead>
              <tbody>
                {(selected.lines || []).map((l: { productId?: { _id?: string; name?: string } | string; quantity?: number }, i: number) => {
                  const pid = typeof l.productId === 'string' ? l.productId : l.productId?._id ?? '';
                  const pname = typeof l.productId === 'string' ? pid : l.productId?.name ?? 'Product';
                  return (
                    <tr key={`${pid}-${i}`} className="border-t border-outline">
                      <td className="px-3 py-2 text-on-surface">{pname}</td>
                      <td className="px-3 py-2 text-right text-on-surface-variant">{l.quantity ?? 0}</td>
                      <td className="px-3 py-2">
                        <Input
                          aria-label={`Return quantity for ${pname}`}
                          type="number"
                          min="0"
                          className="text-right"
                          value={String(quantities[pid] ?? 0)}
                          onChange={(e) => setQuantities(q => ({ ...q, [pid]: Math.max(0, Number(e.target.value) || 0) }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Input label="Reason" name="ret-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Damaged goods, wrong item, …" />
        <p className="text-body-sm text-on-surface-variant">
          Posting the return reverses the received stock and reduces the vendor payable.
        </p>
      </div>
    </Modal>
  );
}

function OrdersTab() {
  const { data, isLoading } = usePurchaseOrders();
  const confirmMut = useConfirmPO();
  const billMut = useBillPO();
  const [createOpen, setCreateOpen] = useState(false);
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const orders = data?.data || [];
  const statusBadge = (s: string) => { const c: Record<string,string> = { draft:'bg-gray-100 text-gray-600', ordered:'bg-blue-100 text-blue-800', partially_received:'bg-yellow-100 text-yellow-800', received:'bg-green-100 text-green-800', billed:'bg-indigo-100 text-indigo-800', paid:'bg-emerald-100 text-emerald-800', cancelled:'bg-red-100 text-red-600' }; return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c[s]||'bg-gray-100'}`}>{s.replace('_',' ')}</span>; };

  return (
    <>
    <NewPurchaseOrderModal open={createOpen} onClose={() => setCreateOpen(false)} />
    <Card className="shadow-xs"><CardHeader title="Purchase Orders" action={<Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1" />New Purchase Order</Button>} /><CardBody className="p-0"><div className="overflow-auto max-h-[500px]"><table className="w-full text-sm text-left"><thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-3">Order #</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Supplier</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
    <tbody className="divide-y">{orders.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No purchase orders.</td></tr>}{orders.map(o => (<tr key={o._id} className="hover:bg-muted/20"><td className="px-4 py-3 font-medium">{o.orderNumber || 'DRAFT'}</td><td className="px-4 py-3">{new Date(o.date).toLocaleDateString('en-IN')}</td><td className="px-4 py-3">{o.supplierId?.name || '-'}</td><td className="px-4 py-3 text-right font-medium">{formatINR(o.grandTotal)}</td><td className="px-4 py-3">{statusBadge(o.status)}</td><td className="px-4 py-3"><div className="flex gap-1">{o.status === 'draft' && <Button size="sm" variant="outline" onClick={() => { confirmMut.mutateAsync(o._id); toast.success('Order confirmed'); }}>Confirm</Button>}{o.status === 'received' && <Button size="sm" variant="outline" onClick={() => { billMut.mutateAsync(o._id).then(() => toast.success('Billed — purchase bill created in Accounts')).catch((err: any) => toast.error(err?.response?.data?.error || 'Billing failed')); }}>Bill</Button>}</div></td></tr>))}</tbody></table></div></CardBody></Card>
    </>
  );
}

function ReceiptsTab() {
  const { data, isLoading } = usePurchaseReceipts();
  const postMut = usePostReceipt();
  const [createOpen, setCreateOpen] = useState(false);
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const receipts = data?.data || [];

  return (
    <>
    <NewReceiptModal open={createOpen} onClose={() => setCreateOpen(false)} />
    <Card className="shadow-xs"><CardHeader title="Goods Received Notes" action={<Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1" />New GRN</Button>} /><CardBody className="p-0"><div className="overflow-auto max-h-[500px]"><table className="w-full text-sm text-left"><thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-3">GRN #</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">PO Ref</th><th className="px-4 py-3 text-right">Qty Received</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
    <tbody className="divide-y">{receipts.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No receipts.</td></tr>}{receipts.map(r => (<tr key={r._id} className="hover:bg-muted/20"><td className="px-4 py-3 font-medium">{r.receiptNumber || 'DRAFT'}</td><td className="px-4 py-3">{new Date(r.date).toLocaleDateString('en-IN')}</td><td className="px-4 py-3">{r.purchaseOrderId?.orderNumber || '-'}</td><td className="px-4 py-3 text-right">{r.totalReceived}</td><td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status==='posted'?'bg-green-100 text-green-800':'bg-gray-100 text-gray-600'}`}>{r.status}</span></td><td className="px-4 py-3">{r.status === 'draft' && <Button size="sm" variant="outline" onClick={() => { postMut.mutateAsync(r._id); toast.success('Receipt posted — stock updated'); }}>Post</Button>}</td></tr>))}</tbody></table></div></CardBody></Card>
    </>
  );
}

function ReturnsTab() {
  const { data, isLoading } = usePurchaseReturns();
  const postMut = usePostReturn();
  const [createOpen, setCreateOpen] = useState(false);
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const returns = data?.data || [];

  return (
    <>
    <NewReturnModal open={createOpen} onClose={() => setCreateOpen(false)} />
    <Card className="shadow-xs"><CardHeader title="Purchase Returns" action={<Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1" />New Return</Button>} /><CardBody className="p-0"><div className="overflow-auto max-h-[500px]"><table className="w-full text-sm text-left"><thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-3">Return #</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">PO Ref</th><th className="px-4 py-3 text-right">Qty Returned</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
    <tbody className="divide-y">{returns.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No returns.</td></tr>}{returns.map(r => (<tr key={r._id} className="hover:bg-muted/20"><td className="px-4 py-3 font-medium">{r.returnNumber || 'DRAFT'}</td><td className="px-4 py-3">{new Date(r.date).toLocaleDateString('en-IN')}</td><td className="px-4 py-3">{r.purchaseOrderId?.orderNumber || '-'}</td><td className="px-4 py-3 text-right">{r.totalReturned}</td><td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status==='posted'?'bg-green-100 text-green-800':'bg-gray-100 text-gray-600'}`}>{r.status}</span></td><td className="px-4 py-3">{r.status === 'draft' && <Button size="sm" variant="outline" onClick={() => { postMut.mutateAsync(r._id); toast.success('Return posted — stock reversed'); }}>Post</Button>}</td></tr>))}</tbody></table></div></CardBody></Card>
    </>
  );
}
